import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { SITELINKS } from "./links";
import { ReactNode } from "react";
import { ToastContainer } from "react-toastify";

import Preloader from "../app-components/preloader/Preloader";
import useAuth from "../api-services/api-query/authentication/useAuth";
import NoRouteMatch from "../pages/NoRouteMatch";
import AdminLayout from "../pages/tepnguser/common/AdminLayout";
import LoginTepngUser from "../pages/tepnguser/auth/LoginTepngUser";
import SegmentForm from "../pages/tepnguser/marine-superintedent/MarineSuitabilityForm";
import ActionTracker from "../pages/tepnguser/tepngadmin/AdminActionTracker";
import ManageSegmentForm from "../pages/tepnguser/tepngadmin/ManageSegmentForm";
import UserManagement from "../pages/tepnguser/tepngadmin/UserManagement";
import VendorManagement from "../pages/tepnguser/tepngadmin/VendorManagement";
import AdminDashbaord from "../pages/tepnguser/tepngadmin/AdminDashboard";
import Policy from "../pages/tepnguser/auth/Policy";
import RoleManagement from "../pages/tepnguser/tepngadmin/RoleManagement";
import VesselInspection from "../pages/tepnguser/marine-superintedent/MarineVesselInspection";
import OperatorLayout from "../pages/vessel-operator/common/OperatorLayout";
import Dashbaord from "../pages/vessel-operator/Dashboard";
import Onboarding from "../pages/vessel-operator/auth/Onboarding";
import LoginVesselOperator from "../pages/vessel-operator/auth/OperatorLogin";
import ResetPassword from "../pages/vessel-operator/auth/ResetPassword";
import UpdatePassword from "../pages/vessel-operator/auth/UpdatePassword";
import VOActionTracker from "../pages/vessel-operator/VOActionTracker";
import VOViewActionTracker from "../pages/vessel-operator/VOViewActionTracker";
import VOCreateActionTracker from "../pages/vessel-operator/VOCreateActionTracker";
import VOVesselInspection from "../pages/vessel-operator/VOVesselInspection";
import VOViewInspection from "../pages/vessel-operator/VOViewInspection";
import MarineDashbaord from "../pages/tepnguser/marine-superintedent/MarineDashboard";
import MarineLayout from "../pages/tepnguser/common/MarineLayout";
import MarineVesselInspection from "../pages/tepnguser/marine-superintedent/MarineVesselInspection";
import MarineSegmentForm from "../pages/tepnguser/marine-superintedent/MarineSuitabilityForm";
import MarineActionTracker from "../pages/tepnguser/marine-superintedent/MarineActionTracker";
import CreateActionTracker from "../pages/tepnguser/marine-superintedent/CreateActionTracker";
import MarineViewActionTracker from "../pages/tepnguser/marine-superintedent/ViewActionTracker";
import AssessmentForm from "../pages/tepnguser/tepngadmin/SuitabilityAssessmentForm";
import ScrollToTop from "../app-components/scroll-to-top/ScrollToTop";
import InspectionHistory from "../pages/tepnguser/tepngadmin/InspectionHistory";
import AdminManual from "../pages/tepnguser/tepngadmin/AdminManual";
import MSManual from "../pages/tepnguser/marine-superintedent/MSManual";
import Manual from "../pages/vessel-operator/Manual";
import ManageDefectCheckList from "../pages/tepnguser/tepngadmin/ManageDefectCheckList";
import AppInsight from "../pages/tepnguser/tepngadmin/AppInsight";
import VesselReport from "../pages/tepnguser/tepngadmin/VesselReport";
import InspectionApprovalHistory from "../pages/tepnguser/tepngadmin/InspectionApprovalHistory";
import MarineInServiceForm from "../pages/tepnguser/marine-superintedent/MarineInServiceForm";
import ManageEquipment from "../pages/tepnguser/tepngadmin/ManageEquipment";
import InServiceAssessmentForm from "../pages/tepnguser/tepngadmin/InServiceAssessmentForm";
import VOViewInServiceInspection from "../pages/vessel-operator/VOViewInServiceInspection";
import ManageFunctionalEmail from "../pages/tepnguser/tepngadmin/ManageFunctionalEmail";
import AdminActionTracker from "../pages/tepnguser/tepngadmin/AdminActionTracker";
import AdminViewActionTracker from "../pages/tepnguser/tepngadmin/AdminViewActionTracker";
import ManageVesselCategory from "../pages/tepnguser/tepngadmin/ManageVesselCategory";
import VesselManagement from "../pages/tepnguser/tepngadmin/VesselManagement";

// Middleware to validate token
function ProtectedAdminRoute({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  const { isAuthenticated, isConsentGiven, loading } = useAuth();

  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  if (!isConsentGiven && isConsentGiven !== null) {
    return <Navigate to={SITELINKS.CONSENT_FORM} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={SITELINKS.SIGNIN} replace />;
  }

  return isAuthenticated ? (
    <AdminLayout title={title}>{children}</AdminLayout>
  ) : (
    <>Unautenticated</>
  );
}

function ProtectedMarineRoute({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  const { isAuthenticated, isConsentGiven, loading } = useAuth();

  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  if (!isConsentGiven && isConsentGiven !== null) {
    return <Navigate to={SITELINKS.CONSENT_FORM} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={SITELINKS.SIGNIN} replace />;
  }

  return isAuthenticated ? (
    <MarineLayout title={title}>{children}</MarineLayout>
  ) : (
    <>Unautenticated</>
  );
}

function ProtectedConsentPage({ children }: { children: ReactNode }) {
  const { isAuthenticated, isConsentGiven, userRoles, loading } = useAuth();

  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  if (isConsentGiven) {
    const rolesSet = new Set(userRoles);
    
    let userView = SITELINKS.SIGNIN;

    if (rolesSet.has("MarineApproval")) {
      userView = SITELINKS.DASHBOARD_ADMIN;
    }
    if (rolesSet.has("MarineSuperintendent")) {
      userView = SITELINKS.DASHBOARD_MARINESUPERINTENDENT;
    }
    if (rolesSet.has("Administrator")) {
      userView = SITELINKS.DASHBOARD_ADMIN;
    }
    if (rolesSet.has("VesselOperator")) {
      userView = SITELINKS.DASHBOARD_VO;
    }

    return <Navigate to={userView} replace />;
  }

  return isAuthenticated ? <>{children}</> : <></>;
}

// Route Guard Component
function ProtectedOperatorRoute({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  const { isAuthenticated, isConsentGiven, loading } = useAuth();

  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  if (!isConsentGiven && isConsentGiven !== null) {
    return <Navigate to={SITELINKS.CONSENT_FORM} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={SITELINKS.MAIN} replace />;
  }

  return isAuthenticated ? (
    <OperatorLayout title={title}>{children}</OperatorLayout>
  ) : (
    <>Unautenticated</>
  );
}

//react-router&&routes
function APPRoutes() {
  return (
    <Router>
      <ScrollToTop />

      <Routes>
        <Route path={SITELINKS.NON} element={<NoRouteMatch />} />
        <Route path={SITELINKS.SIGNIN} element={<LoginTepngUser />} />
        <Route
          path={SITELINKS.CONSENT_FORM}
          element={
            <ProtectedConsentPage>
              <Policy />
            </ProtectedConsentPage>
          }
        />
        <Route
          path={SITELINKS.DASHBOARD_ADMIN}
          element={
            <ProtectedAdminRoute title="Administrator Dashboard">
              <AdminDashbaord />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.MANUAL_TEPNG}
          element={
            <ProtectedAdminRoute title="Administrator Manual">
              <AdminManual />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.USERS}
          element={
            <ProtectedAdminRoute title="Manage User">
              <UserManagement />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.VENDORS}
          element={
            <ProtectedAdminRoute title="Manage Vendors">
              <VendorManagement />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={`${SITELINKS.ASSESSMENT_FORM}/:id`}
          element={
            <ProtectedAdminRoute title="View Form">
              <AssessmentForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.INSERVICE_ASSESSMENT_FORM}/:id`}
          element={
            <ProtectedAdminRoute title="View Form">
              <InServiceAssessmentForm />
            </ProtectedAdminRoute>
          }
        />


        <Route
          path={SITELINKS.INSPECTION_HISTORY}
          element={
            <ProtectedAdminRoute title="Inspection Form History">
              <InspectionHistory />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.DASHBOARD_MARINESUPERINTENDENT}
          element={
            <ProtectedMarineRoute title="Marine Analtyics">
              <MarineDashbaord />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={SITELINKS.MANUAL_MS}
          element={
            <ProtectedMarineRoute title="Marine Manual">
              <MSManual />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={SITELINKS.INSPECTION_MARINESUPERINTENDENT}
          element={
            <ProtectedMarineRoute title="Inspection Form">
              <MarineVesselInspection />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={`${SITELINKS.ASSESSMENT_MARINESUPERINTENDENT}/:id`}
          element={
            <ProtectedMarineRoute>
              <MarineSegmentForm />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={`${SITELINKS.INSERVICE_MARINESUPERINTENDENT}/:id`}
          element={
            <ProtectedMarineRoute>
              <MarineInServiceForm />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={`${SITELINKS.MARINE_ACTION_TRACKER}`}
          element={
            <ProtectedMarineRoute>
              <MarineActionTracker />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={`${SITELINKS.ADMIN_ACTION_TRACKER}`}
          element={
            <ProtectedAdminRoute>
              <AdminActionTracker />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.CREATE_TRACKER}
          element={
            <ProtectedMarineRoute>
              <CreateActionTracker />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={`${SITELINKS.VIEW_TRACKER}/:id`}
          element={
            <ProtectedMarineRoute>
              <MarineViewActionTracker />
            </ProtectedMarineRoute>
          }
        />
        <Route
          path={`${SITELINKS.ADMIN_VIEW_TRACKER}/:id`}
          element={
            <ProtectedAdminRoute>
              <AdminViewActionTracker />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.ROLES}
          element={
            <ProtectedAdminRoute>
              <RoleManagement />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.INSPECTION}
          element={
            <ProtectedAdminRoute>
              <VesselInspection />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={SITELINKS.INSPECTION_APPROVAl_HISTORY}
          element={
            <ProtectedAdminRoute>
              <InspectionApprovalHistory />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.ASSESSMENT}/:id`}
          element={
            <ProtectedAdminRoute>
              <SegmentForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MANAGE_SEGMENT}`}
          element={
            <ProtectedAdminRoute>
              <ManageSegmentForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MANAGE_DEFECT_CHECKLIST}`}
          element={
            <ProtectedAdminRoute>
              <ManageDefectCheckList />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MANAGE_EQUIPMENT}`}
          element={
            <ProtectedAdminRoute>
              <ManageEquipment />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MANAGE_FUNCTIONALEMAIL}`}
          element={
            <ProtectedAdminRoute>
              <ManageFunctionalEmail />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MANAGE_VESSEL_CATEGORY}`}
          element={
            <ProtectedAdminRoute>
              <ManageVesselCategory />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MANAGE_VESSELS}`}
          element={
            <ProtectedAdminRoute>
              <VesselManagement />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.TRACKER}`}
          element={
            <ProtectedAdminRoute>
              <ActionTracker />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.APP_INSIGHT}`}
          element={
            <ProtectedAdminRoute>
              <AppInsight />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.VESSEL_REPORT}`}
          element={
            <ProtectedAdminRoute>
              <VesselReport />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path={`${SITELINKS.MARINESUPERINTENDENT_VESSEL_REPORT}`}
          element={
            <ProtectedMarineRoute>
              <VesselReport />
            </ProtectedMarineRoute>
          }
        />

        {/* VESSEL OPERATOR ROUTES */}
        <Route path={SITELINKS.ONBOARDING} element={<Onboarding />} />
        <Route path={SITELINKS.MAIN} element={<LoginTepngUser />} />
        <Route path={SITELINKS.ROLES} element={<LoginVesselOperator />} />
        <Route path={SITELINKS.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={SITELINKS.UPDATE_PASSWORD} element={<UpdatePassword />} />

        <Route
          path={SITELINKS.DASHBOARD_VO}
          element={
            <ProtectedOperatorRoute title="Vessel Operator Home">
              <Dashbaord />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={SITELINKS.MANUAL_VO}
          element={
            <ProtectedOperatorRoute title="Manual">
              <Manual />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={SITELINKS.VO_TRACKER}
          element={
            <ProtectedOperatorRoute title="Action Tracker">
              <VOActionTracker />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={`${SITELINKS.VO_VIEW_TRACKER}/:id`}
          element={
            <ProtectedOperatorRoute title="View Action Tracker">
              <VOViewActionTracker />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={SITELINKS.VO_CREATE_TRACKER}
          element={
            <ProtectedOperatorRoute title="Action Tracker">
              <VOCreateActionTracker />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={SITELINKS.VO_INSPECTION}
          element={
            <ProtectedOperatorRoute title="Inspection History">
              <VOVesselInspection />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={`${SITELINKS.VO_VIEW_INSPECTION}/:id`}
          element={
            <ProtectedOperatorRoute title="Inspection History">
              <VOViewInspection />
            </ProtectedOperatorRoute>
          }
        />
        <Route
          path={`${SITELINKS.VO_VIEW_INSERVICE}/:id`}
          element={
            <ProtectedOperatorRoute title="Inspection History">
              <VOViewInServiceInspection />
            </ProtectedOperatorRoute>
          }
        />
      </Routes>

      <ToastContainer position="bottom-left" hideProgressBar />
    </Router>
  );
}

export default APPRoutes;
